import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAtom, useAtomValue } from 'jotai'
import { Box, Group, Text, Button } from '@mantine/core'
import { IconRocket, IconSchool, IconUsers } from '@tabler/icons-react'
import { isAuthenticatedAtom, facilitatedGroupsAtom } from '../../atoms/authAtoms'
import { useTeams } from '../../hooks/useTeam'

export function NavHeader() {
  const [isAuthenticated] = useAtom(isAuthenticatedAtom)
  const facilitatedGroups = useAtomValue(facilitatedGroupsAtom)
  const navigate = useNavigate()
  const location = useLocation()

  // "My Team" shortcut — the canonical door back into a team's current story.
  // Team play launches from the team dashboard (which stamps the team identity
  // into the play URL); a global "resume last story" guess was removed because
  // with solo + multiple team plays it pointed at the wrong session. Gate on
  // auth — the nav renders on every page including /login, and firing
  // GET /api/teams there with a stale token 401s into a redirect loop.
  const { data: myTeams = [] } = useTeams({ enabled: isAuthenticated })
  const onTeamScreen = location.pathname.startsWith('/teams')
  const showMyTeam = isAuthenticated && myTeams.length > 0 && !onTeamScreen
  const myTeamTarget = myTeams.length === 1 ? `/teams/${myTeams[0].id}` : '/teams'

  // Facilitators get a shortcut to their group surface (team/member/role
  // management; the per-story release dashboard is reached from there). With
  // several facilitated groups, link to the first (a picker can come later).
  const facilitateTarget = facilitatedGroups[0]
  const onFacilitateScreen =
    facilitateTarget && location.pathname === `/facilitate/group/${facilitateTarget.id}`
  const showFacilitate =
    isAuthenticated && !!facilitateTarget && !onFacilitateScreen

  return (
    <Box
      component="header"
      py="sm"
      px="lg"
      style={{
        borderBottom: '1px solid var(--bw-panel-border)',
        background: 'var(--bw-panel-bg)',
        backdropFilter: 'blur(10px)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}
    >
      <Group justify="space-between">
        <Link to="/" style={{ textDecoration: 'none' }}>
          <Group gap="xs">
            <IconRocket size={24} color="var(--bw-accent)" />
            <Text
              size="lg"
              fw={700}
              ff="var(--bw-heading-font)"
              style={{ color: 'var(--bw-accent)' }}
            >
              BUBOT WORLDS
            </Text>
          </Group>
        </Link>

        <Group gap="sm">
          {showFacilitate && (
            <Button
              variant="light"
              color="grape"
              size="sm"
              leftSection={<IconSchool size={16} />}
              onClick={() => navigate(`/facilitate/group/${facilitateTarget.id}`)}
              title={`Facilitate ${facilitateTarget.name || 'your group'}`}
            >
              Facilitate
            </Button>
          )}
          {showMyTeam && (
            <Button
              variant="light"
              color="teal"
              size="sm"
              leftSection={<IconUsers size={16} />}
              onClick={() => navigate(myTeamTarget)}
              title="Go to your team to play together"
            >
              My Team
            </Button>
          )}
          {isAuthenticated ? (
            <>
              <Button
                variant="subtle"
                color="gray"
                size="sm"
                onClick={() => navigate('/profile')}
              >
                Profile
              </Button>
              <Button
                variant="subtle"
                color="gray"
                size="sm"
                onClick={() => navigate('/settings')}
              >
                Settings
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="subtle"
                color="gray"
                size="sm"
                onClick={() => navigate('/login')}
              >
                Log In
              </Button>
              <Button
                size="sm"
                onClick={() => navigate('/signup')}
              >
                Sign Up
              </Button>
            </>
          )}
        </Group>
      </Group>
    </Box>
  )
}
